import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

/**
 * DOEHome.jsx
 * A Six Sigma DOE (Design of Experiments) home page for MSMEs & Startups.
 * - Hero with quick DOE KPIs (factors, runs, R²)
 * - Quick Actions (Design DOE, Enter Data, Run Analysis, Optimization)
 * - DOE Templates for MSMEs & Startups
 * - Recent Experiments with quick analysis links
 * - Mock Experiment Matrix + Mock ANOVA Results + Example Plots
 */

/* ---------- Small UI helpers ---------- */

const StatCard = ({ label, value, hint }) => (
  <div className="bg-gray-800 border border-white/10 rounded-lg p-4 text-center">
    <div className="text-xs text-gray-300">{label}</div>
    <div className="text-2xl font-semibold text-yellow-200 mt-1">{value}</div>
    {hint && <div className="text-xs text-gray-400 mt-2">{hint}</div>}
  </div>
);

const FeatureCard = ({ title, emoji, desc, to }) => (
  <Link
    to={to || "#"}
    className="block bg-gray-800 p-4 rounded-xl shadow hover:shadow-lg transition"
  >
    <div className="flex flex-row text-lg text-white mb-3">
      {emoji}
      <h3 className="font-semibold text-white text-lg mb-1 ml-2">{title}</h3>
    </div>
    <p className="text-sm text-white">{desc}</p>
  </Link>
);

const DOEExperimentRow = ({ run }) => (
  <tr className="border-b border-gray-600 text-white">
    <td className="px-2 py-1">{run.id}</td>
    <td className="px-2 py-1">{run.factorA}</td>
    <td className="px-2 py-1">{run.factorB}</td>
    <td className="px-2 py-1">{run.response}</td>
  </tr>
);

const ANOVARow = ({ row }) => (
  <tr className="border-b border-gray-600 text-white">
    <td className="px-2 py-1">{row.source}</td>
    <td className="px-2 py-1">{row.df}</td>
    <td className="px-2 py-1">{row.ss}</td>
    <td className="px-2 py-1">{row.ms}</td>
    <td className="px-2 py-1">{row.f}</td>
    <td className="px-2 py-1">{row.p}</td>
  </tr>
);

/* -------------------- Main Component -------------------- */

const DOEHome = () => {
  const authState = useSelector((s) => s.authReducer);
  const user = authState?.user || null;
  const isLoggedIn = !!user;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [userName, setUserName] = useState(user?.name || "");

  // Mock DOE KPIs
  const [kpis, setKpis] = useState({
    factors: 2,
    runs: 4,
    r2: 0.89,
  });

  // Mock experiment matrix
  const [experimentMatrix, setExperimentMatrix] = useState([
    { id: 1, factorA: "Low", factorB: "Low", response: 22 },
    { id: 2, factorA: "High", factorB: "Low", response: 35 },
    { id: 3, factorA: "Low", factorB: "High", response: 28 },
    { id: 4, factorA: "High", factorB: "High", response: 40 },
  ]);

  // Mock ANOVA table
  const [anovaResults, setAnovaResults] = useState([
    { source: "Factor A", df: 1, ss: 120.5, ms: 120.5, f: 9.3, p: 0.018 },
    { source: "Factor B", df: 1, ss: 98.2, ms: 98.2, f: 7.6, p: 0.025 },
    { source: "Interaction A*B", df: 1, ss: 30.1, ms: 30.1, f: 2.3, p: 0.15 },
    { source: "Error", df: 4, ss: 51.8, ms: 12.95, f: "-", p: "-" },
    { source: "Total", df: 7, ss: 300.6, ms: "-", f: "-", p: "-" },
  ]);

  useEffect(() => {
    setUserName(user?.name || "");
  }, [user]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/doe");
  };

  return (
    <div className="min-h-screen flex flex-col bg-black [&_p]:text-white [&_li]:text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">DOE</div>
          <div className="text-[11px] text-gray-200">Design Of Experiments Playground!</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                🔐 
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                <div className="flex gap-3">
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                  >
                    ⏻
                  </button>
                </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
          )}
        </div>
      )}

      <main className="mx-auto px-2 py-4">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center place-items-center">
          <div className="lg:col-span-2 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-lg p-6 shadow-sm">
            <h1 className="text-xl font-bold text-yellow-300">Design of Experiments for MSMEs & Startups</h1>
            <p className="text-sm text-gray-200 mt-2">
              Define factors, run experiments, analyze ANOVA, and optimize your processes using Six Sigma DOE tools.
            </p>

            <div className="mt-6 text-sm grid grid-cols-2 sm:grid-cols-2 gap-2">
              <button className="w-full text-sm px-4 py-2 bg-yellow-600 text-white rounded font-semibold hover:bg-yellow-500 transition">
                Design Experiment
              </button>
              <button className="w-full text-sm px-4 py-2 bg-blue-600  border border-white/20 text-white rounded hover:bg-white/10 transition">
                Enter Data
              </button>
              <button className="w-full text-sm px-4 py-2 bg-yellow-600 border border-white/20 text-white rounded hover:bg-white/10 transition">
                Run Analysis
              </button>
              <button className="w-full text-sm px-4 py-2 bg-blue-600 text-white rounded font-semibold hover:bg-blue-700 transition">
                Optimize Factors
              </button>
            </div>


            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard label="Factors" value={kpis.factors} hint="Independent variables" />
              <StatCard label="Runs" value={kpis.runs} hint="Experimental runs" />
              <StatCard label="R²" value={kpis.r2.toFixed(2)} hint="Model Fit" />
            </div>
          </div>

          {/* Sidebar: DOE Templates */}
          <aside className="bg-black rounded-lg p-4 shadow">
            <h2 className="text-yellow-300 font-semibold">DOE Templates</h2>
            <ul className="mt-3 text-sm space-y-2">
              <li>🍞 Bakery Oven Settings</li>
              <li>👕 Textile Dyeing</li>
              <li>📦 Packaging Drop Test</li>
              <li>💻 SaaS Conversion Funnel</li>
            </ul>
          </aside>
        </section>

        {/* Experiment Matrix */}
        <section className="mt-2 bg-black rounded-lg p-4 shadow">
          <h3 className="text-yellow-200 font-semibold">Mock Experiment Matrix</h3>
          <table className="mt-3 w-full text-sm border border-gray-600">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-2 py-1">Run</th>
                <th className="px-2 py-1">Factor A</th>
                <th className="px-2 py-1">Factor B</th>
                <th className="px-2 py-1">Response</th>
              </tr>
            </thead>
            <tbody>
              {experimentMatrix.map((run) => (
                <DOEExperimentRow key={run.id} run={run} />
              ))}
            </tbody>
          </table>
        </section>

        {/* ANOVA Results */}
        <section className="mt-2 bg-black rounded-lg p-4 shadow">
          <h3 className="text-yellow-200 font-semibold">Mock ANOVA Table</h3>
          <table className="mt-3 w-full text-sm border border-gray-600">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-2 py-1">Source</th>
                <th className="px-2 py-1">DF</th>
                <th className="px-2 py-1">SS</th>
                <th className="px-2 py-1">MS</th>
                <th className="px-2 py-1">F</th>
                <th className="px-2 py-1">P</th>
              </tr>
            </thead>
            <tbody>
              {anovaResults.map((row, idx) => (
                <ANOVARow key={idx} row={row} />
              ))}
            </tbody>
          </table>
        </section>

        {/* Mock Visualization */}
        <section className="mt-2 bg-black rounded-lg p-4 shadow text-center">
          <h3 className="text-yellow-200 font-semibold mb-3">Example Plots</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-100 p-4 rounded">📈 Main Effects Plot (Mock)</div>
            <div className="bg-gray-100 p-4 rounded">🔗 Interaction Plot (Mock)</div>
            <div className="bg-gray-100 p-4 rounded">🌀 Contour Plot (Mock)</div>
          </div>
        </section>
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DOEHome;
